import { type Page, type Response, success, type UserInfo } from "@iflyrpa/share";
import type { SteelAction } from "./index";

/**
 * 头条号登录 RPA
 *
 * 流程：
 * 1. 打开头条号登录页
 * 2. 生成远程调试地址，等待用户扫码
 * 3. 监听 user_info 接口获取用户信息
 * 4. 等待页面跳转到 /profile_v4/index 判断登录成功
 * 5. 收集 cookie 并返回结果
 */
export const rpaAction: SteelAction = async (task, _params) => {
    const reporter = task.reportService?.reportLoginStatus ?? (() => Promise.resolve());

    let page: Page;
    let isSelfClosing = false;
    let handleBrowserDisconnected: (() => void) | undefined;
    const cleanup = async () => {
        if (handleBrowserDisconnected) {
            task._steelBrowser?.off("disconnected", handleBrowserDisconnected);
        }
        if (page && !page.isClosed()) {
            await page.close().catch(() => {});
        }
    };

    // ========== 第一步：启动浏览器并打开登录页 ==========
    try {
        page = await task.createPage({
            url: "https://mp.toutiao.com/auth/page/login",
        });
        if (!task._session?.id) throw new Error("Session ID 不存在");
        handleBrowserDisconnected = () => {
            if (task.debug && isSelfClosing) {
                task.logger.info("执行结束，关闭浏览器！");
            } else {
                task.logger.info("浏览器被手动释放或意外关闭！");
            }
        };
        task._steelBrowser?.on("disconnected", handleBrowserDisconnected);
    } catch (e) {
        return { code: 414, message: `浏览器启动失败: ${e}`, data: {} };
    }

    // ========== 第二步：生成远程调试地址 ==========
    const pageInfo = await task._client?.sessions.liveDetails(task._session.id);
    const pageId = pageInfo?.pages[0].id ?? "";

    const baseHost = task._session.websocketUrl?.replace(/^wss?:\/\//, "") ?? "";
    const connectAddress = new URLSearchParams({ baseHost, pageId }).toString();

    task.reportService?.settingSessionID?.(task._session?.id || "");

    // 拦截欢迎页的图片和视频资源，提升加载速度
    await page.route(/\/welcome.*\.(png|mp4)$/i, (route) => route.abort());

    await reporter(0, "浏览器启动成功，请使用头条号App扫码登录……", { connectAddress });

    // ========== 第三步：监听登录状态与用户信息 ==========
    let userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };
    try {
        /**
         * 监听用户信息接口 /mp/agw/creator_center/user_info
         * 登录成功后该接口会被自动调用，从中提取用户信息
         */
        const handleUserInfo = async (resp: Response) => {
            if (resp.url().includes("/mp/agw/creator_center/user_info")) {
                try {
                    const jsonUserInfo = await resp.json();
                    userInfo = {
                        name: jsonUserInfo.name,
                        avatar: jsonUserInfo.avatar_url,
                        uniqueId: jsonUserInfo.user_id_str,
                    };
                } catch (e) {
                    task.logger.warn("无法解析用户信息:", { error: e });
                }
            }
        };
        page.on("response", handleUserInfo);

        // 等待页面跳转到登录成功页面（不设超时，允许用户慢慢扫码）
        await page.waitForURL("**/profile_v4/index**", {
            timeout: 0,
            waitUntil: "domcontentloaded",
        });

        task.logger.info("页面已跳转到登录成功页面");
        await reporter(0, "登录成功，正在获取用户信息...", { stage: "scanQrcode", connectAddress });

        // 等待用户信息接口响应
        await page.waitForTimeout(2000);

        page.off("response", handleUserInfo);
    } catch (e) {
        task.logger.error("等待登录跳转失败", { error: String(e) });
        isSelfClosing = true;
        await cleanup();
        return { code: 500, message: "登录失败，请重试！", data: { stage: "waitForLogin", connectAddress } };
    }

    // ========== 第四步：收集 cookie 并返回结果 ==========
    const cookie = JSON.stringify(await task._steelBrowserContext?.cookies());
    isSelfClosing = true;
    await cleanup();

    return success({ connectAddress, cookie, userInfo }, "登录成功！");
};
